import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const ecosystems = [
  {
    icon: 'ri-exchange-line', title: '数字资产交易所', desc: '去中心化交易所（DEX）支持NFT、虚拟地产、数字藏品、碳积分等多类资产的安全高效流通。智能合约自动撮合，零中介费用，全球7×24小时不间断运营。',
    value: '$2.8T', label: '年交易额', color: '#00FF88',
    detail: '支持200+数字资产类别，日均交易笔数超过5000万次，最快结算时间0.3秒',
    trend: '+340%',
  },
  {
    icon: 'ri-palette-line', title: 'NFT创作生态', desc: '赋能创作者将数字作品铸造为NFT，实现版权确权与价值变现的全链路支持。AI辅助创作工具让每个人都能成为数字艺术家，版税自动分配机制保障创作者持续收益。',
    value: '1.2亿+', label: 'NFT铸造量', color: '#FFD700',
    detail: '活跃创作者300万+，顶级NFT作品成交价突破1亿美元，创作者平均月收入提升580%',
    trend: '+220%',
  },
  {
    icon: 'ri-building-2-line', title: '虚拟地产市场', desc: '元宇宙土地资产的发行、交易与开发，构建数字世界的不动产经济体系。黄金地段虚拟地产年增值超过300%，商业开发、广告投放、活动举办创造多元收益流。',
    value: '500万+', label: '地块总量', color: '#FF6B9D',
    detail: '顶级地块均价已超过现实城市核心区，虚拟商业地产租金年收益率达15-40%',
    trend: '+180%',
  },
  {
    icon: 'ri-bank-line', title: '去中心化金融', desc: 'DeFi协议支持借贷、流动性挖矿、收益聚合、保险等完整金融服务体系，打造元宇宙金融基础设施。无需KYC，全球任何人都可以平等参与金融活动。',
    value: '$180B', label: 'TVL锁仓量', color: '#00FFCC',
    detail: '年化收益率普遍高于传统金融5-20倍，智能合约审计通过率99.8%，零跑路记录',
    trend: '+450%',
  },
  {
    icon: 'ri-user-star-line', title: '创作者经济', desc: '内容创作者通过元宇宙平台直接触达全球用户，消除中间商剥削，实现创作价值的最大化变现。订阅制、打赏、版税、联名等多元变现模式并行。',
    value: '300万+', label: '活跃创作者', color: '#FF6B35',
    detail: '头部创作者年收入突破千万美元，中腰部创作者平均月收入是传统平台的3.8倍',
    trend: '+290%',
  },
  {
    icon: 'ri-briefcase-4-line', title: '企业元宇宙', desc: '为企业提供虚拟办公、数字展厅、远程协作、产品发布会、员工培训等一站式元宇宙解决方案。降低企业运营成本60%，提升跨国协作效率400%。',
    value: '5000+', label: '企业客户', color: '#A855F7',
    detail: '覆盖制造、金融、教育、医疗、零售等全行业，企业元宇宙ROI平均达到380%',
    trend: '+520%',
  },
];

const economyLayers = [
  {
    layer: '基础层', color: '#00FF88', icon: 'ri-server-line',
    title: '区块链基础设施',
    items: ['以太坊Layer2扩容', 'Solana高性能链', 'IPFS去中心化存储', '跨链桥接协议', '零知识证明系统'],
    desc: '支撑整个数字经济运转的底层技术基础设施，确保数据不可篡改、资产真正归用户所有。',
  },
  {
    layer: '协议层', color: '#FFD700', icon: 'ri-code-s-slash-line',
    title: '智能合约协议',
    items: ['ERC-721 NFT标准', 'ERC-20代币协议', 'DeFi借贷协议', 'DAO治理框架', '数字身份DID'],
    desc: '定义数字经济运行规则的协议层，智能合约自动执行，无需信任第三方。',
  },
  {
    layer: '应用层', color: '#FF6B9D', icon: 'ri-apps-line',
    title: '数字经济应用',
    items: ['NFT交易市场', '虚拟地产平台', 'GameFi游戏经济', '社交代币系统', '数字藏品平台'],
    desc: '直接面向用户的数字经济应用，创造真实的价值流通与用户体验。',
  },
  {
    layer: '价值层', color: '#00FFCC', icon: 'ri-coin-line',
    title: '价值流通体系',
    items: ['数字货币支付', '跨境即时结算', '收益自动分配', '版税追踪系统', '碳积分交易'],
    desc: '确保数字价值能够高效、安全、公平地在全球范围内流通的体系。',
  },
];

const marketData = [
  { year: '2023', value: 800, label: '$8000亿', color: '#FF6B35' },
  { year: '2024', value: 1400, label: '$1.4万亿', color: '#FFD700' },
  { year: '2025', value: 2800, label: '$2.8万亿', color: '#00FF88' },
  { year: '2026E', value: 5000, label: '$5万亿', color: '#00FFCC' },
  { year: '2028E', value: 12000, label: '$12万亿', color: '#A855F7' },
  { year: '2030E', value: 30000, label: '$30万亿', color: '#FF6B9D' },
];

const caseStudies = [
  {
    title: '虚拟时装周',
    brand: 'Gucci × Roblox',
    result: '3天销售额$1200万，虚拟手袋售价超过实体款',
    icon: 'ri-t-shirt-line',
    color: '#FFD700',
    img: 'https://public.readdy.ai/ai/img_res/e24f6210b9bcb8ebed672b459b6b50a7.jpg',
  },
  {
    title: '元宇宙演唱会',
    brand: 'Travis Scott × Fortnite',
    result: '2770万人同时在线，收入$2000万，打破吉尼斯纪录',
    icon: 'ri-music-2-line',
    color: '#FF6B9D',
    img: 'https://readdy.ai/api/search-image?query=metaverse%20concert%20virtual%20music%20festival%20holographic%20stage%20millions%20viewers%20neon%20glow%20dark%20background%20futuristic%20cinematic&width=400&height=240&seq=case-2&orientation=landscape',
  },
  {
    title: '数字土地开发',
    brand: 'Decentraland',
    result: '单块虚拟土地成交价$243万，商业地产年租金收益率35%',
    icon: 'ri-building-2-line',
    color: '#00FF88',
    img: 'https://readdy.ai/api/search-image?query=virtual%20real%20estate%20metaverse%20land%20development%20digital%20city%20Decentraland%20neon%20glow%20dark%20background%20futuristic%20cinematic&width=400&height=240&seq=case-3&orientation=landscape',
  },
];

const newsItems = [
  { tag: '投融资', title: 'META66生态基金完成首期10亿美元募集，重点布局6G与Web5.0赛道', time: '2小时前', hot: true, color: '#FF6B35' },
  { tag: '技术', title: '量子加密通信协议v3.0发布，元宇宙数据安全迈入新纪元', time: '5小时前', hot: false, color: '#00FFCC' },
  { tag: '政策', title: '国家元宇宙发展规划正式出台，6G商用时间表明确至2026年', time: '1天前', hot: true, color: '#00FF88' },
  { tag: 'XR', title: 'META Vision Pro 2代发布，8K分辨率+全身触觉反馈颠覆行业', time: '2天前', hot: false, color: '#A855F7' },
  { tag: 'Web5.0', title: '去中心化身份协议DID 2.0标准获全球50+机构联署支持', time: '3天前', hot: false, color: '#FFD700' },
  { tag: '数字经济', title: '全球元宇宙经济总量突破5万亿美元，超越多个G20国家GDP', time: '4天前', hot: true, color: '#FF6B9D' },
];

export default function DigitalEconomy() {
  const [activeLayer, setActiveLayer] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: overviewRef, isVisible: overviewVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: layerRef, isVisible: layerVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: caseRef, isVisible: caseVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: newsRef, isVisible: newsVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="economy" className="relative py-24 md:py-32 overflow-hidden" style={{ background: '#020810' }}>
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full opacity-4" style={{ background: 'radial-gradient(ellipse, #00FF88 0%, transparent 70%)' }} />
        <div className="absolute top-20 left-0 w-[300px] h-[300px] rounded-full opacity-4" style={{ background: 'radial-gradient(ellipse, #FFD700 0%, transparent 70%)' }} />
        <div className="absolute inset-0 opacity-2" style={{
          backgroundImage: 'linear-gradient(rgba(0,255,136,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.06) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }} />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-14 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 mb-6 rounded-full" style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.25)' }}>
            <i className="ri-coin-line text-[#00FF88] text-sm" />
            <span className="text-[#00FF88] text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>Digital Economy Ecosystem</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-5" style={{ fontFamily: "'Orbitron', monospace" }}>
            数字经济
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] to-[#FFD700]"> 生态</span>
          </h2>
          <p className="text-white/40 text-base max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            元宇宙数字经济正在重构人类的价值创造与流通体系。从NFT到DeFi，从虚拟地产到创作者经济，
            一个全新的数字文明经济形态正在崛起，其规模将在2030年超越传统实体经济。
          </p>
        </div>

        {/* Market Growth Chart */}
        <div
          ref={overviewRef as React.RefObject<HTMLDivElement>}
          className={`mb-14 transition-all duration-700 delay-100 ${overviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Chart */}
            <div className="p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                <i className="ri-line-chart-line text-[#00FF88]" />
                全球元宇宙经济规模增长
              </h3>
              <div className="flex items-end gap-3 h-40">
                {marketData.map((d, i) => (
                  <div key={d.year} className="flex-1 flex flex-col items-center gap-2">
                    <span className="text-xs font-bold" style={{ color: d.color, fontFamily: "'Orbitron', monospace", fontSize: '0.6rem' }}>{d.label}</span>
                    <div className="w-full rounded-t-lg transition-all duration-700 relative overflow-hidden"
                      style={{
                        height: `${(d.value / 30000) * 120}px`,
                        background: `linear-gradient(180deg, ${d.color}, ${d.color}40)`,
                        minHeight: '8px',
                        transitionDelay: `${i * 100}ms`,
                      }}>
                      <div className="absolute inset-0 opacity-30" style={{ background: 'linear-gradient(180deg, white, transparent)' }} />
                    </div>
                    <span className="text-white/40 text-xs" style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.65rem' }}>{d.year}</span>
                  </div>
                ))}
              </div>
              <p className="text-white/30 text-xs mt-4 text-center" style={{ fontFamily: "'Inter', sans-serif" }}>数据来源：IDC · Gartner · META66研究院 · E=预测值</p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '$5万亿', label: '2026年全球元宇宙经济规模', icon: 'ri-global-line', color: '#00FF88', sub: '同比增长180%' },
                { value: '28亿', label: '全球数字经济参与者', icon: 'ri-user-line', color: '#FFD700', sub: '年增长率45%' },
                { value: '45%', label: '年复合增长率CAGR', icon: 'ri-line-chart-line', color: '#FF6B9D', sub: '超越所有传统行业' },
                { value: '2030', label: '超越实体经济预测年', icon: 'ri-time-line', color: '#00FFCC', sub: '届时规模$30万亿+' },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col gap-3 p-5 rounded-2xl" style={{ background: `${stat.color}06`, border: `1px solid ${stat.color}20` }}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: `${stat.color}20` }}>
                    <i className={`${stat.icon} text-lg`} style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="font-black text-2xl" style={{ color: stat.color, fontFamily: "'Orbitron', monospace" }}>{stat.value}</p>
                    <p className="text-white/40 text-xs leading-tight mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>{stat.label}</p>
                    <p className="text-xs mt-1 font-semibold" style={{ color: stat.color, opacity: 0.7, fontFamily: "'Inter', sans-serif" }}>{stat.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Ecosystem Grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16 transition-all duration-700 delay-150 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {ecosystems.map((e, i) => (
            <div
              key={e.title}
              className="group p-6 transition-all duration-300 hover:-translate-y-1 cursor-pointer rounded-2xl"
              style={{
                background: `${e.color}05`,
                border: `1px solid ${e.color}18`,
                transitionDelay: `${i * 60}ms`,
              }}
              onMouseEnter={(el) => {
                (el.currentTarget as HTMLElement).style.borderColor = `${e.color}45`;
                (el.currentTarget as HTMLElement).style.background = `${e.color}10`;
              }}
              onMouseLeave={(el) => {
                (el.currentTarget as HTMLElement).style.borderColor = `${e.color}18`;
                (el.currentTarget as HTMLElement).style.background = `${e.color}05`;
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ background: `${e.color}20` }}>
                  <i className={`${e.icon} text-xl`} style={{ color: e.color }} />
                </div>
                <div className="text-right">
                  <p className="font-black text-xl" style={{ color: e.color, fontFamily: "'Orbitron', monospace" }}>{e.value}</p>
                  <p className="text-white/30 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{e.label}</p>
                  <p className="text-xs font-bold mt-0.5" style={{ color: e.color, fontFamily: "'Inter', sans-serif" }}>{e.trend} YoY</p>
                </div>
              </div>
              <h3 className="text-white font-bold text-base mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{e.title}</h3>
              <p className="text-white/40 text-sm leading-relaxed mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>{e.desc}</p>
              <div className="p-3 rounded-xl text-xs" style={{ background: `${e.color}08`, border: `1px solid ${e.color}15` }}>
                <i className="ri-information-line mr-1" style={{ color: e.color }} />
                <span className="text-white/50" style={{ fontFamily: "'Inter', sans-serif" }}>{e.detail}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Economy Layers */}
        <div
          ref={layerRef as React.RefObject<HTMLDivElement>}
          className={`mb-16 transition-all duration-700 delay-200 ${layerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Orbitron', monospace" }}>
              数字经济<span className="text-[#00FF88]">四层架构</span>
            </h3>
            <p className="text-white/40 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>从底层基础设施到价值流通，构建完整的数字经济生态</p>
          </div>
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {economyLayers.map((l, i) => (
              <button key={l.layer} onClick={() => setActiveLayer(i)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                style={{
                  background: activeLayer === i ? `${l.color}20` : 'rgba(255,255,255,0.04)',
                  border: `1.5px solid ${activeLayer === i ? l.color : 'rgba(255,255,255,0.1)'}`,
                  color: activeLayer === i ? l.color : 'rgba(255,255,255,0.4)',
                  fontFamily: "'Inter', sans-serif",
                }}>
                <i className={l.icon} />
                {l.layer} · {l.title}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="p-6 rounded-2xl" style={{ background: `${economyLayers[activeLayer].color}06`, border: `1px solid ${economyLayers[activeLayer].color}25` }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl" style={{ background: `${economyLayers[activeLayer].color}20` }}>
                  <i className={`${economyLayers[activeLayer].icon} text-2xl`} style={{ color: economyLayers[activeLayer].color }} />
                </div>
                <div>
                  <p className="text-xs font-bold tracking-widest uppercase" style={{ color: economyLayers[activeLayer].color, fontFamily: "'Inter', sans-serif" }}>{economyLayers[activeLayer].layer}</p>
                  <h4 className="text-white font-black text-lg" style={{ fontFamily: "'Inter', sans-serif" }}>{economyLayers[activeLayer].title}</h4>
                </div>
              </div>
              <p className="text-white/60 text-sm leading-relaxed mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>{economyLayers[activeLayer].desc}</p>
              <div className="flex flex-wrap gap-2">
                {economyLayers[activeLayer].items.map(item => (
                  <span key={item} className="px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: `${economyLayers[activeLayer].color}15`, color: economyLayers[activeLayer].color, border: `1px solid ${economyLayers[activeLayer].color}30`, fontFamily: "'Inter', sans-serif" }}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {economyLayers.map((l, i) => (
                <div key={l.layer} className="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all" onClick={() => setActiveLayer(i)}
                  style={{ background: activeLayer === i ? `${l.color}10` : 'rgba(255,255,255,0.02)', border: `1px solid ${activeLayer === i ? l.color + '40' : 'rgba(255,255,255,0.06)'}` }}>
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: `${l.color}20` }}>
                    <i className={`${l.icon} text-sm`} style={{ color: l.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-white/80 text-sm font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>{l.layer} · {l.title}</p>
                    <p className="text-white/35 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{l.items.slice(0, 3).join(' · ')}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-white/30" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Case Studies */}
        <div
          ref={caseRef as React.RefObject<HTMLDivElement>}
          className={`mb-16 transition-all duration-700 delay-200 ${caseVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Orbitron', monospace" }}>
              标杆<span className="text-[#FFD700]">案例</span>
            </h3>
            <p className="text-white/40 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>真实发生的数字经济奇迹</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {caseStudies.map(c => (
              <div key={c.title} className="rounded-2xl overflow-hidden group cursor-pointer" style={{ border: `1px solid ${c.color}25` }}>
                <div className="relative h-40 overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(2,8,16,0.9), transparent)' }} />
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 rounded-full text-xs font-bold" style={{ background: `${c.color}20`, color: c.color, border: `1px solid ${c.color}40`, fontFamily: "'Inter', sans-serif" }}>
                      {c.brand}
                    </span>
                  </div>
                </div>
                <div className="p-4" style={{ background: `${c.color}05` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${c.color}20` }}>
                      <i className={`${c.icon} text-sm`} style={{ color: c.color }} />
                    </div>
                    <h4 className="text-white font-bold text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>{c.title}</h4>
                  </div>
                  <p className="text-white/60 text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{c.result}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Future Outlook */}
        <div className="mb-16 p-8 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(0,255,136,0.05), rgba(255,215,0,0.03), rgba(255,107,157,0.05))', border: '1px solid rgba(0,255,136,0.15)' }}>
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1">
              <h3 className="text-white font-bold text-xl mb-4 flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                <span style={{ color: '#00FF88' }}>✦</span> 数字经济综述
              </h3>
              <p className="text-white/50 text-sm leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                元宇宙数字经济是人类经济史上最深刻的范式转变。它不仅仅是将现实经济搬到虚拟空间，而是创造了全新的价值生产方式——在数字世界中，创意即是生产力，注意力即是货币，身份即是资产。
              </p>
              <p className="text-white/40 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                Web5.0去中心化协议确保了数字资产的真正所有权，区块链技术实现了价值的无摩擦流通，AI创作工具让每个人都成为内容创作者。这三者的融合，正在构建一个比现实世界更公平、更高效、更具创造力的数字经济体系。
              </p>
            </div>
            <div className="flex-1">
              <h3 className="text-white font-bold text-xl mb-4 flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                <span style={{ color: '#FFD700' }}>✦</span> 未来展望
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  { year: '2027', text: '元宇宙数字经济规模突破1万亿美元，NFT市场成熟化，数字资产与实体资产法律地位等同', color: '#FF6B35' },
                  { year: '2030', text: 'Web5.0数字身份成为全球通行证，数字资产与实体资产完全互通，DeFi规模超越传统金融', color: '#A855F7' },
                  { year: '2035', text: '数字经济超越实体经济，成为全球最大经济形态，元宇宙GDP超越多数G20国家', color: '#00FFCC' },
                ].map((item) => (
                  <div key={item.year} className="flex items-start gap-3 p-3 rounded-xl" style={{ background: `${item.color}08`, border: `1px solid ${item.color}15` }}>
                    <span className="font-black text-sm flex-shrink-0 mt-0.5" style={{ color: item.color, fontFamily: "'Orbitron', monospace" }}>{item.year}</span>
                    <p className="text-white/45 text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* News Section */}
        <div
          ref={newsRef as React.RefObject<HTMLDivElement>}
          className={`border-t border-white/6 pt-14 transition-all duration-700 delay-200 ${newsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "'Inter', sans-serif" }}>
              <i className="ri-newspaper-line text-[#00FF88]" />
              行业<span className="text-[#00FF88] ml-1">资讯</span>
            </h3>
            <Link to="/news" className="flex items-center gap-2 text-[#00FF88] text-sm font-semibold hover:gap-3 transition-all duration-300 whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif" }}>
              <span>查看全部</span>
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {newsItems.map((n) => (
              <Link
                to="/news"
                key={n.title}
                className="group flex items-start gap-4 p-4 transition-all duration-300 cursor-pointer rounded-xl"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = `${n.color}30`;
                  (e.currentTarget as HTMLElement).style.background = `${n.color}04`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.06)';
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)';
                }}
              >
                <div className="flex flex-col items-center gap-2 flex-shrink-0">
                  <span className="px-2 py-0.5 text-xs font-semibold whitespace-nowrap rounded-full" style={{ background: `${n.color}15`, color: n.color, fontFamily: "'Inter', sans-serif" }}>
                    {n.tag}
                  </span>
                  {n.hot && (
                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full" style={{ background: 'rgba(255,107,53,0.15)', color: '#FF6B35', fontFamily: "'Inter', sans-serif" }}>
                      HOT
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white/80 text-sm leading-relaxed group-hover:text-white transition-colors duration-200 line-clamp-2" style={{ fontFamily: "'Inter', sans-serif" }}>{n.title}</p>
                  <p className="text-white/30 text-xs mt-2" style={{ fontFamily: "'Inter', sans-serif" }}>{n.time}</p>
                </div>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <i className="ri-arrow-right-line text-[#00FF88] text-sm" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
